/**
 * Fixtures tipadas do Admin PWA — cenário de teste da Frente E.
 *
 * Mantidas em `packages/ui` (não em `apps/admin`) para que outras frentes
 * (ex.: QA/Release em `tests/`) possam importar o mesmo cenário sem
 * duplicar dados. Quando `apps/api` existir, um adapter real deve
 * implementar a mesma forma (`ConversationListItem[]`, `Customer360`,
 * `CommandCenterStats`) e esta fixture passa a ser usada apenas em
 * testes (regra 5 do protocolo de execução paralela).
 */
import { assertCommandCenterStats, assertConversationDetail, assertConversationListItem, assertCustomer360, assertLeadScoreDetail } from "./types.js";
export const fixtureConversations = [
    {
        id: "conv_1001",
        customerId: "cust_01",
        customerName: "Marina Oliveira",
        channel: "whatsapp",
        status: "open",
        lastMessagePreview: "Quero saber se o laser 20W corta acrílico de 10mm",
        lastMessageAt: "2026-10-05T12:40:00.000Z",
        leadScore: 82,
        assignedTo: "ai",
        unreadCount: 1
    },
    {
        id: "conv_1002",
        customerId: "cust_02",
        customerName: "Carlos Pereira",
        channel: "whatsapp",
        status: "pending",
        lastMessagePreview: "Preciso falar com um humano sobre garantia",
        lastMessageAt: "2026-10-05T11:55:00.000Z",
        leadScore: 54,
        assignedTo: "human",
        unreadCount: 2
    },
    {
        id: "conv_1003",
        customerId: "cust_03",
        customerName: "Fernanda Lima",
        channel: "webchat",
        status: "open",
        lastMessagePreview: "Qual a diferença entre o kit DIY MK2 e o pronto?",
        lastMessageAt: "2026-10-05T10:20:00.000Z",
        leadScore: 65,
        assignedTo: "ai",
        unreadCount: 0
    },
    {
        id: "conv_1004",
        customerId: "cust_04",
        customerName: "João Souza",
        channel: "instagram",
        status: "snoozed",
        lastMessagePreview: "Me avisa quando tiver desconto",
        lastMessageAt: "2026-10-04T18:05:00.000Z",
        leadScore: 30,
        assignedTo: "unassigned",
        unreadCount: 0
    },
    {
        id: "conv_1005",
        customerId: "cust_05",
        customerName: "Patrícia Souza",
        channel: "email",
        status: "closed",
        lastMessagePreview: "Obrigada, pedido recebido!",
        lastMessageAt: "2026-10-03T09:15:00.000Z",
        leadScore: 95,
        assignedTo: "ai",
        unreadCount: 0
    }
];
fixtureConversations.forEach(assertConversationListItem);
export const fixtureCustomer360 = {
    customerId: "cust_01",
    name: "Marina Oliveira",
    phone: "5511988887777",
    email: "marina.oliveira@example.com",
    leadScore: 82,
    leadStage: "proposta",
    totalOrders: 2,
    totalSpentCents: 489000,
    lastInteractionAt: "2026-10-05T12:40:00.000Z",
    tags: ["laser-20w", "recorrente", "sudeste"],
    conversationIds: ["conv_1001", "conv_0890"],
    auditLog: [
        {
            id: "audit_001",
            occurredAt: "2026-10-05T12:40:05.000Z",
            actorType: "ai",
            actorLabel: "Sales AI",
            action: "AI_RESPONSE_GENERATED",
            details: "Respondeu pergunta técnica sobre corte de acrílico 10mm no laser 20W."
        },
        {
            id: "audit_002",
            occurredAt: "2026-10-05T09:12:00.000Z",
            actorType: "system",
            actorLabel: "Lead Intelligence Engine",
            action: "LEAD_SCORE_CHANGED",
            details: "Lead score ajustado de 74 para 82 após segunda interação qualificada em 48h."
        },
        {
            id: "audit_003",
            occurredAt: "2026-09-30T14:02:00.000Z",
            actorType: "human",
            actorLabel: "Vendedor Rafael",
            action: "HUMAN_TAKEOVER",
            details: "Assumiu a conversa para negociar frete; devolveu para IA após 6 minutos."
        }
    ]
};
assertCustomer360(fixtureCustomer360);
/**
 * Calcula os indicadores do Command Center (item 57) a partir das
 * conversas disponíveis. Mantido como função pura (não fixture estática)
 * para que o mesmo cálculo possa ser reaplicado quando a lista vier da
 * API real — só a fonte dos dados muda, o contrato de saída não.
 */
export function computeCommandCenterStats(conversations, revenueTodayCents) {
    const open = conversations.filter((c) => c.status === "open" || c.status === "pending");
    const pendingHumanTakeover = conversations.filter((c) => c.status === "pending" && c.assignedTo === "human").length;
    const averageLeadScore = conversations.length
        ? Math.round(conversations.reduce((sum, c) => sum + c.leadScore, 0) / conversations.length)
        : 0;
    const aiHandled = conversations.filter((c) => c.assignedTo === "ai").length;
    const aiResolutionRatePercent = conversations.length
        ? Math.round((aiHandled / conversations.length) * 100)
        : 0;
    const stats = {
        openConversations: open.length,
        pendingHumanTakeover,
        averageLeadScore,
        aiResolutionRatePercent,
        conversationsLast24h: conversations.length,
        revenueTodayCents
    };
    assertCommandCenterStats(stats);
    return stats;
}
export const fixtureCommandCenterStats = computeCommandCenterStats(fixtureConversations, 489000);
/**
 * Conversation detail (item 84 — Conversation Timeline) + AI Response
 * Preview (item 137). A última mensagem é um `aiDraft` ainda não enviado,
 * com confiança e fonte citada — a UI deve exibir isso como preview, não
 * como mensagem enviada (ver `ConversationMessage.aiDraft` em types.ts).
 */
export const fixtureConversationDetail = {
    id: "conv_1001",
    customerId: "cust_01",
    customerName: "Marina Oliveira",
    channel: "whatsapp",
    status: "open",
    messages: [
        {
            id: "msg_001",
            sender: "customer",
            senderLabel: "Marina Oliveira",
            text: "Quero saber se o laser 20W corta acrílico de 10mm",
            sentAt: "2026-10-05T12:38:00.000Z"
        },
        {
            id: "msg_002",
            sender: "ai",
            senderLabel: "Sales AI",
            text: "Sim! O laser 20W corta acrílico de até 10mm em uma passada, com velocidade reduzida.",
            sentAt: "2026-10-05T12:39:10.000Z"
        },
        {
            id: "msg_003",
            sender: "customer",
            senderLabel: "Marina Oliveira",
            text: "E qual a espessura máxima recomendada pra não perder qualidade de corte?",
            sentAt: "2026-10-05T12:40:00.000Z"
        },
        {
            id: "msg_004",
            sender: "ai",
            senderLabel: "Sales AI",
            text: "",
            sentAt: "2026-10-05T12:40:05.000Z",
            aiDraft: {
                text: "Recomendamos até 8mm para corte limpo em uma única passada; acima disso, o acabamento das bordas pode ficar com mais marcas de queima.",
                confidencePercent: 87,
                citedSource: "Manual técnico Laser 20W — seção 'Limites de corte por material', rev. 2026-03"
            }
        }
    ]
};
assertConversationDetail(fixtureConversationDetail);
/**
 * Lead Score detail: score atual + histórico simples de eventos. Os valores
 * são consistentes com `fixtureCustomer360.auditLog` (mesmo cliente), mas
 * mantidos como contrato próprio porque o Lead Score detail é uma tela
 * independente do Customer 360 (pode ser aberta a partir do Inbox também).
 */
export const fixtureLeadScoreDetail = {
    customerId: "cust_01",
    customerName: "Marina Oliveira",
    currentScore: 82,
    history: [
        {
            id: "score_evt_001",
            occurredAt: "2026-09-30T14:02:00.000Z",
            previousScore: 60,
            newScore: 74,
            reason: "Negociação de frete conduzida por humano (HUMAN_TAKEOVER)."
        },
        {
            id: "score_evt_002",
            occurredAt: "2026-10-05T09:12:00.000Z",
            previousScore: 74,
            newScore: 82,
            reason: "Segunda interação qualificada em 48h (pergunta técnica específica)."
        }
    ]
};
assertLeadScoreDetail(fixtureLeadScoreDetail);
export * from "./types.js";
